import React, { ChangeEvent, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import QueryViewer from "components/queries/QueryViewer";
import LoadSavedQueryButton from "components/uiElements/inputs/buttons/LoadSavedQueryButton";
import NewQueryButton from "components/uiElements/inputs/buttons/NewQueryButton";
import DayOfWeekSelector, { DayOfWeekValue } from "components/uiElements/inputs/DayOfWeekSelector";
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from "system/APIRequest";
import { Query, socialDatasetId } from "system/query/Query";
import { QueryFieldTypes, QueryInfo } from "system/query/QueryInfo";
import { AlertSensitivity, QueryMonitor } from "../../system/QueryMonitor";
import { TextLoader } from "components/apiHandling/TextLoader";

interface QueryMonitorDialogProps {
  open: boolean;
  onChange(): void;
  onClose(): void;
  queryMonitor?: QueryMonitor | null;
}

export default function QueryMonitorDialog(props: QueryMonitorDialogProps): React.JSX.Element {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [infegySocialQueryInfo, setInfegySocialQueryInfo] = useState<QueryInfo | null>(null);
  const [title, setTitle] = useState<string>(props.queryMonitor?.title ?? "");
  const [description, setDescription] = useState<string>(props.queryMonitor?.description ?? "");
  const [queryJSON, setQueryJSON] = useState<Object | null>(props.queryMonitor?.query ?? null);
  const [alertSensitivity, setAlertSensitivity] = useState<AlertSensitivity>(props.queryMonitor?.alert_sensitivity ?? "medium");
  const [digestDays, setDigestDays] = useState<Array<DayOfWeekValue>>(props.queryMonitor?.getDigestWeekdaysValues() ?? []);
  const [saveError, setSaveError] = useState<string>("");
  const [isLoadingQuery, setIsLoadingQuery] = useState<boolean>(false);
  const [isLoadingQueryInfo, setIsLoadingQueryInfo] = useState<boolean>(false);

  const query: Query = new Query({ query: queryJSON ?? {} });
  const isQueryEmpty = !query.query.op;
  
  // On first initialize
  useEffect(() => {
    getInfegySocialQueryInfo();
  }, []);

  async function getInfegySocialQueryInfo() {
    try {
      setIsLoadingQueryInfo(true);
      const response = await APIRequest(`query-info/${socialDatasetId}`, {}, {method: "GET"});
      setInfegySocialQueryInfo(response);
    } catch (e) {
      console.error(`Error getting social dataset for id ${socialDatasetId}: ${e}`);
      Sentry.captureException(e);
    } finally {
      setIsLoadingQueryInfo(false);
    }
  }

  async function handleSave() {
    setSaveError("");
    // If the alert sensitivity is "none" and there are no digest days, the Query Monitor will never run.
    const isOperational = !(alertSensitivity === "none" && digestDays.length === 0);
    const hasRequiredFields = !!title && !!query && queryJSON && !!Object.keys(queryJSON).length;
    if (!hasRequiredFields) {
      setSaveError(t("query-monitor-dialog.errors.missing-required-field"));
      return;
    }
    if (!isOperational) {
      setSaveError(t("query-monitor-dialog.errors.invalid-config"));
      return;
    }

    try {
      const values = {
        alert_sensitivity: alertSensitivity,
        description,
        digest_weekdays: digestDays,
        query: queryJSON,
        title
      };
      if (props.queryMonitor?.id) {
        await QueryMonitor.update(props.queryMonitor.id, values);
      } else {
        await QueryMonitor.create(values);
      }
      enqueueSnackbar(t("query-monitor-dialog.save-success-toast"), { variant: "success" });
      props.onChange();
      props.onClose();
    } catch (e) {
      const error = e as APIError;
      enqueueAPIErrorSnackbar(error, enqueueSnackbar);
      console.error(`Error saving or updating Query Monitor: ${error.message}`);
      Sentry.captureException(e);
    }
  }

  async function handleQueryChange(query: Query | null) {
    query?.id && getQuery(query?.id);
  }
  
  async function getQuery(queryId: string) {
    try {
      setIsLoadingQuery(true);
      const response = await APIRequest(`obj/query/${queryId}`);
      setQueryJSON(response.output.query);
    } catch (e) {
      console.error(`Error loading Query id ${queryId}: ${e}`);
      Sentry.captureException(e);
    } finally {
      setIsLoadingQuery(false);
    }
  }

  const timestampFields = infegySocialQueryInfo?.fields.filter((field) => field.type === QueryFieldTypes.timestamp) ?? [];
  const queryString = JSON.stringify(query);
  let hasTimestampField = false;
  for (const field of timestampFields) {
    if (queryString.includes(`"field":"${field.id}"`)) {
      hasTimestampField = true;
      break;
    }
  }

  return (
    <ResponsiveDialog maxWidth="sm" onClose={props.onClose} open={props.open} >
      <DialogTitle>
        <Typography variant="h3">
          {t("query-monitor-dialog.title")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box>
          <SimpleFormLabel>
            {t("query-monitor-dialog.fields.title")}
          </SimpleFormLabel>
          <SimpleFormTextField 
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setTitle(event.target.value)}
            value={title} />
        </Box>
        <Box>
          <SimpleFormLabel>
            {t("query-monitor-dialog.fields.description")}
          </SimpleFormLabel>
          <SimpleFormTextField 
            multiline={true} 
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
            rows={1}
            value={description} />
        </Box>
        <Box sx={{
          marginBottom: "15px"
        }}>
          <SimpleFormLabel>
            {t("query-monitor-dialog.fields.query.label")}
          </SimpleFormLabel>
          {(isLoadingQuery || isLoadingQueryInfo) &&
            <Box sx={{
              textAlign: "center"
            }}>
              <TextLoader />
            </Box>
            || infegySocialQueryInfo && !isQueryEmpty &&
            <Box
              sx={{
                maxHeight: "150px",
                overflow: "auto"
              }}>
              <QueryViewer query={query} queryInfo={infegySocialQueryInfo}/>
            </Box>
            ||
            <Alert severity="info">
              {t("query-monitor-dialog.fields.query.help-text")}
            </Alert>
          }
          <Box sx={{
            marginTop: 1
          }}>
            <LoadSavedQueryButton
              onChange={(query: Query) => handleQueryChange(query)} 
              query={isQueryEmpty ? null : query} />
            <NewQueryButton 
              hideQueryTitle={true}
              fixedDatasetId={socialDatasetId}
              onChange={(query: Query) => handleQueryChange(query)} 
              query={isQueryEmpty ? null : query} />
            <Button
              color="error"
              onClick={() => setQueryJSON(null)}
              size={"small"}
              sx={{ ml: 1 }}>
              {i18n.t('query-form.clear-query')}
            </Button>
          </Box>
        </Box>
        <Box sx={{
          marginBottom: "15px"
        }}>
          <SimpleFormLabel>
            {t("query-monitor-dialog.fields.digest-days")}
          </SimpleFormLabel>
          <DayOfWeekSelector days={digestDays} onChange={(days) => setDigestDays(days)} />
        </Box>
        <Box>
          <SimpleFormLabel>
            {t("query-monitor-dialog.fields.alert-sensitivity.label")}
          </SimpleFormLabel>
          <Select sx={{ minWidth: "120px" }}
            onChange={(event: SelectChangeEvent<AlertSensitivity>) => setAlertSensitivity(event.target.value as AlertSensitivity)}
            value={alertSensitivity} >
            <MenuItem value={"high"}>{t("query-monitor-dialog.fields.alert-sensitivity.options.high")}</MenuItem>
            <MenuItem value={"medium"}>{t("query-monitor-dialog.fields.alert-sensitivity.options.medium")}</MenuItem>
            <MenuItem value={"low"}>{t("query-monitor-dialog.fields.alert-sensitivity.options.low")}</MenuItem>
            <MenuItem value={"none"}>{t("query-monitor-dialog.fields.alert-sensitivity.options.none")}</MenuItem>
          </Select>
        </Box>
      </DialogContent>
      {!!saveError.length &&
        <Alert severity="error" sx={{marginLeft: 1, marginRight: 1}}>{saveError}</Alert>
      }
      {hasTimestampField &&
        <Alert severity="warning" sx={{marginLeft: 1, marginRight: 1}}>{t("query-monitor-dialog.timestamp-warning")}</Alert>
      }
      <DialogActions>
        <Button 
          color="secondary"
          onClick={() => props.onClose()}
          variant="contained">
            {t("cancel")}
        </Button>
        <Button 
          color="success"
          onClick={() => handleSave()}
          variant="contained" >
            {t("save")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}